// @engine/core — TierDetector (REBUILD_PLAN Step 4b).
// Two pure, unit-testable functions — NO global reads inside the classifiers.
// Inject the renderer string and navigator-ish fields; the callers supply globals.
//
// ENGINE CITATIONS (verified against playcanvas@2.19.5 / graphics-device-create.js):
//   • createGraphicsDevice auto-appends 'webgl2' and 'null' if missing (graphics-device-create.js:7-12).
//   • Trying WebGPU requires `window?.navigator?.gpu` truthy (graphics-device-create.js:16).
//   • Created WebGPU device: `_deviceType = 'webgpu'`, `isWebGPU = true` (webgpu-graphics-device.js:79-80).
//   • Created WebGL2 device: `_deviceType = 'webgl2'`, `isWebGPU = false` (webgl-graphics-device.js:155).
//   • `device.deviceType` is a getter returning `_deviceType` (graphics-device.js:371-372).
//
// FRAMEWORK STANCE (DOC-3 / REBUILD_PLAN Step 4b / architecture.md §2.1.1 / performance.md §1.1):
//   • A standard-chunk-only game MAY pass ['webgpu','webgl2'] — createGraphicsDevice falls back free.
//   •  passes contentWebgpuEligible:false (every material is procedural/shader, webgpuEligible:false,
//     gate-14). Production request stays ['webgl2']. The ?gfx=webgpu dev override exercises the gate-14 throw.
//   • WGSL twins + the §14 pixel-equivalence gate are the FUTURE unlock, not a launch requirement.

/** Options for resolving the device-type list to pass to createGraphicsDevice. */
export interface ResolveDeviceTypesOpts {
  /**
   * Whether every material in this game/content bundle is WebGPU-eligible (i.e. uses only standard-chunk
   * materials with shipping WGSL twins — no custom GLSL chunks). When false, the production request is
   * forced to ['webgl2'] regardless of the browser's WebGPU capability.
   *
   *  passes `false` (all materials are procedural/shader, gate-14, WebGL2-pinned until WGSL twins
   * pass the §14 pixel-equivalence gate).
   *
   * A future standard-chunk game would pass `true` to get ['webgpu','webgl2'] and free mobile fallback.
   */
  contentWebgpuEligible: boolean;
  /**
   * Dev/test override. 'webgpu' → ['webgpu','webgl2'] (exercises the gate-14 tripwire on ;
   * the standard WebGPU path on an eligible game). 'webgl2' → ['webgl2'] (explicit baseline lockdown).
   * null / undefined → governed by contentWebgpuEligible.
   */
  force?: string | null;
}

/**
 * Resolve the device-type list to pass to `createGraphicsDevice`.
 *
 * FRAMEWORK RULE:
 *   • contentWebgpuEligible:true → ['webgpu','webgl2'] (createGraphicsDevice falls back to webgl2 on mobile).
 *   • contentWebgpuEligible:false → ['webgl2'] (the game's procedural look, gate-14).
 *   • force='webgpu' → ['webgpu','webgl2'] regardless — the DEV override that exercises the gate-14 tripwire.
 *   • force='webgl2' → ['webgl2'] — explicit baseline lockdown.
 *
 * Pure and unit-testable (no global reads).
 *
 * @example
 *   // main.ts — production ()
 *   const gfx = new URLSearchParams(window.location.search).get('gfx');
 *   const deviceTypes = resolveDeviceTypes({ contentWebgpuEligible: false, force: gfx });
 *   // → always ['webgl2'] in production; ['webgpu','webgl2'] when ?gfx=webgpu
 */
export function resolveDeviceTypes(opts: ResolveDeviceTypesOpts): string[] {
  const { contentWebgpuEligible, force } = opts;
  if (force === 'webgpu') return ['webgpu', 'webgl2'];
  if (force === 'webgl2') return ['webgl2'];
  return contentWebgpuEligible ? ['webgpu', 'webgl2'] : ['webgl2'];
}

// ─── Tier detection ─────────────────────────────────────────────────────────

/** Quality tier. DPR APPLICATION is deliberately left to the caller (Step 4c). */
export type QualityTier = 'low' | 'mid' | 'desktop';

/** The result of detectTier — the raw classification. Applying maxPixelRatio to device.maxPixelRatio is Step 4c. */
export interface TierResult {
  tier: QualityTier;
  /** Suggested `device.maxPixelRatio` for this tier. See note below. */
  maxPixelRatio: number;
}

/**
 * Minimal navigator-ish fields for testing. Pass the real `navigator` at call sites.
 * Only the fields the classifier reads are required.
 */
export interface NavigatorHint {
  userAgent?: string;
  /** `navigator.maxTouchPoints > 0` → likely mobile. */
  maxTouchPoints?: number;
  /**
   * `window.matchMedia('(pointer: fine)').matches` — fine pointer → likely desktop mouse.
   * Pass `true` for desktop, `false` for touch, `undefined` if unknown.
   */
  hasFinePointer?: boolean;
  /**
   * Raw `window.devicePixelRatio` at classification time (the cap `Math.min(2, dpr)` is applied
   * only for desktop tier; mobile tiers return fixed values regardless of dpr).
   */
  devicePixelRatio?: number;
}

// ─── GPU string matchers ─────────────────────────────────────────────────────

/**
 * Software renderers: SwiftShader, llvmpipe, ANGLE-on-SwiftShader, Mesa.
 * These report "desktop" UA but should not get a DPR above 1 (CI must not change).
 */
const RE_SOFTWARE = /swiftshader|llvmpipe|software rasterizer|microsoft basic render|angle.*swiftshader/i;

/**
 * Known-low mobile GPU strings → tier 'low', DPR 1.0.
 * Adreno 3xx–6xx, Mali-T7xx/G5x, Apple A12/A13 (iPhone SE A13 is the fragile target).
 * Deliberately broad on the conservative side: unknown → 1.0.
 */
const RE_KNOWN_LOW = /adreno.*(3[0-9]{2}|4[0-9]{2}|5[0-9]{2}|6[0-9]{2})\b|mali-t7[0-9]{2}|mali-g5[0-9]\b|apple a12\b|apple a13\b/i;

/**
 * Mid-tier allowlist — DEVICE-LANE-PENDING (these GPUs have NOT been confirmed on a tethered device
 * at p10 FPS ≥ 28; they are assigned mid/1.5 based on spec comparisons only). Only clearly-capable
 * mobile GPUs appear here; anything doubtful stays at low/1.0.
 *
 * @device-lane-pending: validate Adreno 730/740, Mali-G710+, Apple A15/A16/M-series on real hardware
 *   before widening this allowlist. A false positive here causes the DPR 1.5 write to a weak device
 *   (the opposite direction from the safe default).
 */
const RE_MID_ALLOWLIST =
  /adreno.*(7[0-9]{2})\b|mali-g7[0-9]{2}|mali-g8[0-9]{2}|mali-g9[0-9]{2}|apple a1[5-9]\b|apple a[2-9][0-9]\b|apple m[1-9]\b/i;

/**
 * Desktop GPU strings — RTX, Radeon RX, Intel Iris Xe, Intel Arc, NVIDIA Quadro, etc.
 * A GPU matching this is treated as desktop regardless of UA heuristics (in case of user-agent spoofing).
 */
const RE_DESKTOP_GPU =
  /nvidia geforce|rtx [0-9]|gtx [0-9]|radeon rx|intel(r)? iris xe|intel(r)? arc|intel(r)? uhd|apple m[1-9]\b|quadro|firepro|vega [0-9]/i;

// ─── Tier classifier ─────────────────────────────────────────────────────────

/**
 * Classify the device tier from the unmasked GPU renderer string and navigator-ish hints.
 *
 * Conservative strategy: anything unknown → 'low'/1.0 (the safe direction; a weak device at DPR 1.5
 * is a frame-budget violation; a strong device at DPR 1.0 is just slightly under-utilised).
 *
 * DPR APPLICATION is deliberately NOT done here — this function returns `maxPixelRatio`, and the
 * caller applies it to `device.maxPixelRatio` in Step 4c. Separating classification from application
 * makes this function pure and testable.
 *
 * @param device - Object with the optional `unmaskedRenderer` string (from `device.unmaskedRenderer`
 *   on a WebGL2 device via `WEBGL_debug_renderer_info`; may be empty string on privacy-restricted browsers).
 * @param nav - Optional navigator-ish hints. Omit in tests that focus on the GPU string path.
 */
export function detectTier(
  device: { unmaskedRenderer?: string },
  nav?: NavigatorHint,
): TierResult {
  const renderer = device.unmaskedRenderer ?? '';
  const dpr = nav?.devicePixelRatio ?? 1;

  // 1. Software renderer — treat as desktop geometry/draw budget but cap DPR at 1 (no real GPU).
  if (RE_SOFTWARE.test(renderer)) {
    return { tier: 'desktop', maxPixelRatio: 1 };
  }

  // 2. Known-low mobile GPU → low/1.0 (the fragile gate target: iPhone SE A13 / Adreno 610).
  if (RE_KNOWN_LOW.test(renderer)) {
    return { tier: 'low', maxPixelRatio: 1.0 };
  }

  // 3. Mid allowlist — clearly-capable mobile GPU (DEVICE-LANE-PENDING).
  if (RE_MID_ALLOWLIST.test(renderer)) {
    return { tier: 'mid', maxPixelRatio: 1.5 };
  }

  // 4. Desktop GPU string — strong signal regardless of UA.
  if (RE_DESKTOP_GPU.test(renderer)) {
    return { tier: 'desktop', maxPixelRatio: Math.min(2, dpr) };
  }

  // 5. UA/pointer heuristics — only when GPU string gives no confident signal (empty or unknown).
  //    Fine-pointer (mouse) + non-mobile UA → desktop.
  const ua = nav?.userAgent ?? '';
  const isMobileUA = /android|iphone|ipad|ipod|mobile/i.test(ua);
  const hasFinePointer = nav?.hasFinePointer ?? false;
  const maxTouchPoints = nav?.maxTouchPoints ?? 0;

  if (hasFinePointer && !isMobileUA && maxTouchPoints === 0) {
    // Desktop UA with fine pointer and no touch → desktop tier.
    return { tier: 'desktop', maxPixelRatio: Math.min(2, dpr) };
  }

  // 6. Anything else (unknown GPU, mobile UA, touch device, ambiguous) → low/1.0 (CONSERVATIVE).
  return { tier: 'low', maxPixelRatio: 1.0 };
}
